import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { kritischeAenderungBestaetigen } from "../utils/veranstaltungstag";
import { mitSitzungspruefung } from "../utils/fetchWithAuth";

export default function Systemverwaltung() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("token");

  // === System-Reset ===
  const resetAllResults = async () => {
    if (!(await kritischeAenderungBestaetigen("ALLE Ergebnisse löschen."))) return;
    const confirmed = window.confirm(
      "⚠️ Bist du sicher, dass du ALLE Ergebnisse löschen möchtest?\n" +
        "Dieser Vorgang kann nicht rückgängig gemacht werden!"
    );
    if (!confirmed) return;

    const verification = prompt(
      'Bitte gib zur Bestätigung "RESET" ein (Großbuchstaben):'
    );
    if (verification !== "RESET") {
      alert("Aktion abgebrochen. Eingabe war nicht korrekt.");
      return;
    }

    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/admin/reset`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }));

      if (!res.ok) throw new Error("Fehler beim Zurücksetzen der Ergebnisse");
      const data = await res.json();
      alert(data.message || "Reset erfolgreich!");
    } catch (err) {
      console.error(err);
      alert("❌ Fehler beim Reset: " + (err.message || err));
    }
  };

  // === Komplett-Reset für das neue Jahr ===
  //
  // Zwei getrennte, unwiderrufliche Löschungen – jeweils eigenes doppeltes
  // Bestätigungs-Gate (confirm + Text-Eingabe), damit ein Fehlklick nichts
  // auslösen kann. Bewusst getrennte Buttons statt eines gemeinsamen: Teams
  // löschen (neue Anmeldungen fürs neue Jahr) und Stationen löschen (neue
  // Spiele) sind unabhängige Entscheidungen.
  const [loadingResetTeams, setLoadingResetTeams] = useState(false);
  const [loadingResetStations, setLoadingResetStations] = useState(false);

  const resetAllTeams = async () => {
    if (!(await kritischeAenderungBestaetigen("ALLE Teams endgültig löschen."))) return;
    const confirmed = window.confirm(
      "⚠️⚠️ ALLE TEAMS ENDGÜLTIG LÖSCHEN?\n\n" +
        "Damit verschwinden sämtliche Teams samt ihrer Ergebnisse unwiderruflich. " +
        "Stationen (Spielbeschreibungen) bleiben erhalten.\n\n" +
        "Nur für den Start einer NEUEN Veranstaltung ausführen!"
    );
    if (!confirmed) return;

    const verification = prompt(
      'Bitte gib zur Bestätigung "TEAMS LÖSCHEN" ein:'
    );
    if (verification !== "TEAMS LÖSCHEN") {
      alert("Aktion abgebrochen. Eingabe war nicht korrekt.");
      return;
    }

    setLoadingResetTeams(true);
    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/admin/reset-teams`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Fehler beim Löschen aller Teams");
      alert(data.message || "Alle Teams wurden gelöscht.");
    } catch (err) {
      console.error(err);
      alert("❌ Fehler beim Löschen aller Teams: " + (err.message || err));
    } finally {
      setLoadingResetTeams(false);
    }
  };

  const resetAllStations = async () => {
    if (!(await kritischeAenderungBestaetigen("ALLE Stationen endgültig löschen."))) return;
    const confirmed = window.confirm(
      "⚠️⚠️ ALLE STATIONEN ENDGÜLTIG LÖSCHEN?\n\n" +
        "Damit verschwinden sämtliche Stationen samt Spielbeschreibung, Regeln " +
        "und Ergebnissen unwiderruflich. Teams bleiben erhalten, ihre Punkte " +
        "werden auf 0 zurückgesetzt.\n\n" +
        "Nur für den Start einer NEUEN Veranstaltung ausführen!"
    );
    if (!confirmed) return;

    const verification = prompt(
      'Bitte gib zur Bestätigung "STATIONEN LÖSCHEN" ein:'
    );
    if (verification !== "STATIONEN LÖSCHEN") {
      alert("Aktion abgebrochen. Eingabe war nicht korrekt.");
      return;
    }

    setLoadingResetStations(true);
    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/admin/reset-stations`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Fehler beim Löschen aller Stationen");
      alert(data.message || "Alle Stationen wurden gelöscht.");
    } catch (err) {
      console.error(err);
      alert("❌ Fehler beim Löschen aller Stationen: " + (err.message || err));
    } finally {
      setLoadingResetStations(false);
    }
  };

  return (
    <div className="p-6 bg-secondary-950 text-white min-h-screen">
      <h1 className="text-2xl font-bold mb-6">Systemverwaltung</h1>

      {/* === System-Reset === */}
      <div className="bg-secondary-900 border-2 border-red-900 rounded-lg p-4 flex flex-col items-start mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Trash2 className="text-red-500" />
          <h2 className="font-semibold text-lg">System-Reset</h2>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          Löscht alle Ergebnisse, Punkte und Berechnungen. Nur für neue
          Veranstaltung ausführen. Ergebnis-Freigabe und Live-Ansicht werden
          dabei ausgeschaltet, Doppeltreffer-Entscheidungen verworfen.
        </p>
        <button
          onClick={resetAllResults}
          className="w-full px-4 py-3 rounded bg-red-700 hover:bg-red-600 text-white font-semibold transition-colors flex items-center justify-center gap-2"
        >
          <Trash2 size={18} />
          ⚠️ Alle Ergebnisse löschen
        </button>
      </div>

      {/* === Komplett-Reset fürs neue Jahr === */}
      <div className="bg-secondary-900 border-2 border-red-900 rounded-lg p-4 flex flex-col items-start">
        <div className="flex items-center gap-3 mb-2">
          <Trash2 className="text-red-500" />
          <h2 className="font-semibold text-lg">Komplett-Reset für das neue Jahr</h2>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          Löscht Teams bzw. Stationen dauerhaft aus der Datenbank – nicht nur die
          Punkte. Jede Aktion verlangt eine eigene Bestätigung per Texteingabe
          und lässt sich danach nicht rückgängig machen. Ergebnis-Freigabe und
          Live-Ansicht werden ebenfalls ausgeschaltet; die Preisregeln bleiben.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
          <div>
            <button
              disabled={loadingResetTeams}
              onClick={resetAllTeams}
              className="w-full px-4 py-3 rounded bg-red-800 hover:bg-red-700 text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Trash2 size={18} />
              {loadingResetTeams ? "wird gelöscht…" : "⚠️ Alle Teams löschen"}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Stationen (Spielbeschreibungen) bleiben erhalten.
            </p>
          </div>

          <div>
            <button
              disabled={loadingResetStations}
              onClick={resetAllStations}
              className="w-full px-4 py-3 rounded bg-red-800 hover:bg-red-700 text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Trash2 size={18} />
              {loadingResetStations ? "wird gelöscht…" : "⚠️ Alle Stationen löschen"}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Teams bleiben erhalten, ihre Punkte werden auf 0 gesetzt.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
